// src/App.jsx

import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { Layout, Button, Drawer } from "antd";
import { ReactComponent as Icon } from "./StomataScopeLogo.svg";

import Top from "./Top";
import Login from "./Login";
import Storage from "./Storage";
import Charts from "./Charts";
import UsersManual from "./UsersManual";
import Download from "./Download";
import ResetPassword from "./ResetPassword";
import BoundingBox from "./BoundingBox";
import EditPage from "./EditPage";
import ProfilePage from "./ProfilePage";
import Mypage from "./Mypage";
import SideMenu from "./SideMenu";
import TermsPage from "./TermsPage";
import AdminRoute from "./AdminRoute";
import AdminPanel from "./AdminPanel";
import AuthGate from "./AuthGate";
import AppLoader from "./AppLoader";
import Analysis from "./Analysis";

import { useState, useEffect } from "react";
import "./i18n";
import { useTranslation } from "react-i18next";

import ScrollToTop from "./components/ScrollToTop";

/* =========================
   公開ページ
========================= */

import CaseStudies from "./public/CaseStudies";
import CaseStudy01 from "./public/CaseStudy01";
import CaseStudy02 from "./public/CaseStudy02";
import CaseStudy03 from "./public/CaseStudy03";
import CaseStudy04 from "./public/CaseStudy04";

const { Header, Sider, Content, Footer } = Layout;

/* =========================
   認証後アプリレイアウト
========================= */

function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { ready } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isMobile === null || !ready) {
    return <AppLoader variant="layout" />;
  }

  return (
    <Layout
      style={{
        minHeight: "100vh",
      }}
    >
      {isMobile ? (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{
            padding: 0,
          }}
          width="70%"
          styles={{
            header: {
              display: "none",
            },
          }}
        >
          <SideMenu />
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth="0"
          style={{
            backgroundColor: "white",
            height: "100vh",
          }}
        >
          <SideMenu />
        </Sider>
      )}

      <Layout>
        <Header className="app-header">
          <div className="header-content">
            <Button
              type="text"
              icon={
                isMobile ? (
                  <MenuOutlined />
                ) : collapsed ? (
                  <MenuOutlined />
                ) : (
                  <CloseOutlined />
                )
              }
              onClick={() =>
                isMobile
                  ? setDrawerVisible(true)
                  : setCollapsed(!collapsed)
              }
              className="menu-button"
            />

            <Icon className="app-logo" />

            <div className="app-title">
              Stomata Scope
            </div>
          </div>
        </Header>

        <Content className="scrollable-container">
          <Routes>
            <Route
              index
              element={<Mypage />}
            />

            <Route
              path="Top"
              element={<Top />}
            />

            <Route
              path="storage"
              element={<Storage />}
            />

            <Route
              path="charts"
              element={<Charts />}
            />

            <Route
              path="usersManual"
              element={<UsersManual />}
            />

            <Route
              path="analysis"
              element={<Analysis />}
            />

            <Route
              path="Download"
              element={<Download />}
            />

            <Route
              path="BoundingBox"
              element={<BoundingBox />}
            />

            <Route
              path="edit/:photoId"
              element={<EditPage />}
            />

            <Route
              path="profile"
              element={<ProfilePage />}
            />

            {/* =========================
                管理者
            ========================= */}

            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
          </Routes>
        </Content>

        <Footer
          style={{
            textAlign: "center",
            height: 30,
            padding: 2,
          }}
        >
          <a
            href="https://happy-quality.jp/"
            target="_blank"
            rel="noreferrer"
            className="webLink"
          >
            Copyright2025 by Happy Quality Co.,Ltd.
          </a>
        </Footer>
      </Layout>
    </Layout>
  );
}

/* =========================
   App ルート
========================= */

function App() {
  const [showBootLoader, setShowBootLoader] = useState(true);
  const [bootFadeOut, setBootFadeOut] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setBootFadeOut(true);
    }, 500);

    const removeTimer = setTimeout(() => {
      setShowBootLoader(false);
    }, 850);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  return (
    <>
      <BrowserRouter>
        <ScrollToTop />

        <Routes>

          {/* =========================
              🔓 公開ページ
              AuthGateの外
          ========================= */}

          <Route
            path="/case-studies"
            element={<CaseStudies />}
          />

          <Route
            path="/case-studies/01"
            element={<CaseStudy01 />}
          />

          <Route
            path="/case-studies/02"
            element={<CaseStudy02 />}
          />

          <Route
            path="/case-studies/03"
            element={<CaseStudy03 />}
          />

          <Route
            path="/case-studies/04"
            element={<CaseStudy04 />}
          />

          {/* =========================
              🔓 既存の認証不要ページ
          ========================= */}

          <Route
            path="/login"
            element={<Login />}
          />

          <Route
            path="/ResetPassword"
            element={<ResetPassword />}
          />

          <Route
            path="/terms"
            element={<TermsPage />}
          />

          {/* =========================
              🔐 既存アプリ
              URLは一切変更しない
          ========================= */}

          <Route
            path="/*"
            element={
              <AuthGate>
                <AppLayout />
              </AuthGate>
            }
          />

        </Routes>
      </BrowserRouter>

      {showBootLoader && (
        <AppLoader
          variant="boot"
          fadeOut={bootFadeOut}
        />
      )}
    </>
  );
}

export default App;